import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, ArrowRight, Eye, EyeOff, Menu, Send } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  buildNotesMailto,
  loadNotes,
  loadReadSet,
  loadReviewBatches,
  loadReviewLibrary,
  ORIGINAL_REVIEW_BATCH,
  paintParagraph,
  saveNotes,
  saveReadSet,
} from '@/lib/dojoReviewLibrary';

const NOTES_TO = 'alex@lightningstudios.ai';

const requestedBatch = () => new URLSearchParams(window.location.search).get('batch') || ORIGINAL_REVIEW_BATCH;

/**
 * DojoReviewPage — /dojo-review — the reading room for the unit library.
 *
 * Built for one job: a reviewer at the HOW Institute reading every vignette
 * the way a learner meets it (arrive → consider → story → your move), one per
 * screen, with the answer key hidden until asked for. Public and unlinked:
 * the content is hypothetical vignettes and reviewer notes, and login friction
 * is the fastest way to lose a reviewer. Notes live in the reviewer's browser
 * and leave as one email.
 */
export default function DojoReviewPage() {
  const batches = useMemo(() => loadReviewBatches(), []);
  const [batchId, setBatchId] = useState(requestedBatch);
  const batch = batches.find((entry) => entry.id === batchId);
  const items = useMemo(() => loadReviewLibrary(batchId), [batchId]);
  const [pos, setPos] = useState(0);
  const [read, setRead] = useState<Set<string>>(() => loadReadSet());
  const [notes, setNotes] = useState<Record<string, string>>(() => loadNotes());
  const [revealed, setRevealed] = useState(false);
  const [railOpen, setRailOpen] = useState(false);

  const item = items[pos];

  const selectBatch = (next: string) => {
    const url = new URL(window.location.href);
    if (next === ORIGINAL_REVIEW_BATCH) url.searchParams.delete('batch');
    else url.searchParams.set('batch', next);
    window.history.pushState(null, '', url);
    setBatchId(next);
    setPos(0);
    setRevealed(false);
    setRailOpen(false);
  };

  useEffect(() => {
    const restoreBatch = () => {
      setBatchId(requestedBatch());
      setPos(0);
      setRevealed(false);
      setRailOpen(false);
    };
    window.addEventListener('popstate', restoreBatch);
    return () => window.removeEventListener('popstate', restoreBatch);
  }, []);

  const go = (next: number) => {
    if (next < 0 || next >= items.length) return;
    setPos(next);
    setRevealed(false);
    setRailOpen(false);
    try {
      window.scrollTo({ top: 0 });
    } catch {
      /* non-browser environments */
    }
  };
  const markReadAndNext = () => {
    if (!item) return;
    const nextRead = new Set(read);
    nextRead.add(item.id);
    setRead(nextRead);
    saveReadSet(nextRead);
    if (pos < items.length - 1) go(pos + 1);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'TEXTAREA' || tag === 'INPUT' || tag === 'SELECT') return;
      if (e.key === 'ArrowRight' || e.key === 'j') markReadAndNext();
      if (e.key === 'ArrowLeft' || e.key === 'k') go(pos - 1);
      if (e.key === 'r') setRevealed((r) => !r);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pos, read, item]);

  if (!item) {
    return (
      <div className="min-h-screen bg-how-paper p-10 text-how-text-on-paper">
        <p>{batch ? 'No vignettes are available in this review batch yet.' : 'This review batch is not available.'}</p>
        <button type="button" onClick={() => selectBatch(ORIGINAL_REVIEW_BATCH)} className="mt-4 underline">
          Open the original library
        </button>
      </div>
    );
  }

  const noteCount = items.filter((entry) => notes[entry.id]?.trim()).length;
  const readCount = items.filter((entry) => read.has(entry.id)).length;
  const progress = Math.round((100 * readCount) / items.length);

  return (
    <div className="min-h-screen bg-how-paper text-how-text-on-paper">
      <button
        type="button"
        onClick={() => setRailOpen((o) => !o)}
        className="fixed left-3 top-3 z-30 inline-flex items-center gap-2 rounded-full border border-how-space-deep/15 bg-white px-3 py-2 text-xs font-semibold text-how-space-deep shadow-sm md:hidden"
        aria-label="Open the list of vignettes"
      >
        <Menu className="h-4 w-4" /> All {items.length}
      </button>

      <div className="grid min-h-screen md:grid-cols-[19rem_1fr]">
        <aside
          className={cn(
            'fixed inset-y-0 left-0 z-20 w-[min(20rem,86vw)] overflow-y-auto border-r border-how-space-deep/10 bg-how-paper-soft p-4 transition-transform md:sticky md:top-0 md:h-screen md:w-auto md:translate-x-0',
            railOpen ? 'translate-x-0' : '-translate-x-full',
          )}
        >
          <div className="font-heading text-lg uppercase tracking-wide text-how-space-deep">Practice review</div>
          <label htmlFor="review-batch" className="mb-1 mt-4 block text-xs font-semibold text-how-text-secondary">
            Review edition
          </label>
          <select
            id="review-batch"
            value={batchId}
            onChange={(event) => selectBatch(event.target.value)}
            className="mb-3 w-full rounded-md border border-how-space-deep/20 bg-white p-2 text-sm text-how-space-deep focus:outline-none focus:ring-2 focus:ring-how-space-deep"
          >
            {batches.map((entry) => <option key={entry.id} value={entry.id}>{entry.label}</option>)}
          </select>
          {batch?.status === 'review_draft' && (
            <div className="mb-3 rounded-md border border-how-space-deep/15 bg-white p-3 text-xs leading-relaxed">
              <p className="font-semibold text-how-space-deep">Review drafts · not approved for pilot use</p>
              {batch.description && <p className="mt-1 text-how-text-secondary">{batch.description}</p>}
              {batch.variantCount < batch.expectedVariantCount && (
                <p className="mt-1 text-how-text-secondary">{batch.variantCount} of {batch.expectedVariantCount} planned vignettes available.</p>
              )}
            </div>
          )}
          <p className="mb-3 mt-1 text-xs text-how-text-secondary">
            {items.length} vignettes · read them in order · your place and notes stay in this browser
          </p>
          <div className="mb-4 h-1 overflow-hidden rounded-full bg-how-space-deep/10">
            <div className="h-full bg-how-accent-warm transition-all" style={{ width: `${progress}%` }} />
          </div>
          <nav aria-label="Vignettes">
            {items.map((it, i) => {
              const first = i === 0 || items[i - 1].imperative !== it.imperative;
              return (
                <div key={it.id}>
                  {first && (
                    <div className="mb-1 mt-4 text-[0.66rem] font-bold uppercase tracking-[0.12em] text-how-accent-warm">
                      {it.unitNumber}. {it.imperative}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => go(i)}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-how-accent-warm/10',
                      i === pos && 'bg-white font-semibold text-how-space-deep shadow-sm ring-1 ring-how-space-deep/10',
                    )}
                    aria-current={i === pos ? 'page' : undefined}
                  >
                    <span
                      className={cn(
                        'grid h-4 w-4 flex-none place-items-center rounded-full border text-[0.6rem] text-white',
                        read.has(it.id) ? 'border-emerald-700 bg-emerald-700' : 'border-how-space-deep/25',
                      )}
                      aria-label={read.has(it.id) ? 'read' : 'unread'}
                    >
                      {read.has(it.id) ? '✓' : ''}
                    </span>
                    <span className="min-w-0 flex-1 truncate">
                      {it.level} · {it.leader}
                    </span>
                    <span className="text-[0.68rem] tabular-nums text-how-text-secondary">{it.words}w</span>
                    {notes[it.id]?.trim() && <span className="h-1.5 w-1.5 rounded-full bg-how-accent-warm" aria-label="has a note" />}
                  </button>
                </div>
              );
            })}
          </nav>
          {noteCount > 0 && (
            <a
              href={buildNotesMailto(items, notes, NOTES_TO, batch)}
              className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-how-space-deep px-4 py-2 text-sm font-semibold text-white"
            >
              <Send className="h-4 w-4" /> Send {noteCount} note{noteCount === 1 ? '' : 's'} to Alex
            </a>
          )}
        </aside>

        <main className="max-w-[56rem] px-6 pb-24 pt-16 md:px-12 md:pt-10" aria-live="polite">
          {batch?.status === 'review_draft' && (
            <p className="mb-5 text-sm font-semibold text-how-space-deep">{batch.label} · review draft</p>
          )}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-how-text-secondary">
            <span className="font-bold uppercase tracking-[0.08em] text-how-accent-warm">{item.imperative}</span>
            <span>{item.level}</span>
            <span>{item.words} words</span>
            <span>
              {pos + 1} of {items.length}
            </span>
          </div>
          <h1 className="mb-1 mt-2 font-heading text-4xl uppercase leading-none text-how-space-deep md:text-5xl">{item.title}</h1>
          <p className="mb-8 text-sm text-how-text-secondary">
            Leader: {item.leader}. Read it the way a learner would — questions first, then the story, then your move.
          </p>

          <Beat>Arrive</Beat>
          {item.intake.map((q) => (
            <Quote key={q}>{q}</Quote>
          ))}

          <Beat>Consider as you read</Beat>
          <Quote>{item.reactWell}</Quote>
          <Quote>{item.reactBetter}</Quote>

          <Beat>The story</Beat>
          <div className="max-w-[64ch] font-serif text-[1.15rem] leading-[1.78]">
            {item.exemplar.map((p, i) => (
              <p key={i} className="mb-5" data-testid={`exemplar-${i}`}>
                {revealed
                  ? paintParagraph(p, item.spans, i).map((seg, k) =>
                      'mark' in seg ? (
                        <span key={k}>
                          <mark
                            data-mark={seg.mark}
                            className={cn(
                              'rounded-sm px-0.5 text-inherit',
                              seg.mark === 'g' && 'bg-emerald-200/70 shadow-[inset_0_-2px_0_theme(colors.emerald.700)]',
                              seg.mark === 'y' && 'bg-amber-200/70 shadow-[inset_0_-2px_0_theme(colors.amber.600)]',
                              seg.mark === 'cost' && 'border-b-2 border-dotted border-amber-600 bg-transparent px-0',
                            )}
                          >
                            {seg.text}
                          </mark>
                          <sup className={cn('ml-0.5 font-sans text-[0.62rem] font-extrabold', seg.mark === 'g' ? 'text-emerald-800' : 'text-amber-800')}>
                            {seg.tag}
                          </sup>
                        </span>
                      ) : (
                        <span key={k}>{seg.text}</span>
                      ),
                    )
                  : p}
              </p>
            ))}
          </div>

          <Beat>Your move</Beat>
          <span className="mb-3 inline-block rounded-full bg-how-accent-warm/10 px-3 py-1 text-xs font-bold text-how-accent-warm">
            Variable flipped: {item.flipAxis}
          </span>
          <div className="max-w-[64ch] font-serif text-[1.15rem] leading-[1.78]">
            {item.enact.map((p, i) => (
              <p key={i} className="mb-5">
                {p}
              </p>
            ))}
            <p className="mt-2 border-t border-how-space-deep/10 pt-4 text-[1.18rem] font-medium leading-snug text-how-space-deep">{item.question}</p>
          </div>

          <div className="mt-9 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => setRevealed((r) => !r)}
              className="inline-flex items-center gap-2 rounded-full bg-how-accent-warm px-4 py-2.5 text-sm font-semibold text-white"
            >
              {revealed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              {revealed ? 'Hide the answer key' : 'Reveal the answer key'}
            </button>
            <span className="text-xs text-how-text-secondary">← → to move · r to reveal</span>
          </div>

          {revealed && (
            <div className="mt-6 rounded-xl border border-how-space-deep/10 bg-white p-5" data-testid="answer-key">
              <h3 className="mb-3 text-[0.7rem] font-bold uppercase tracking-[0.13em] text-how-text-secondary">
                Where Waldo hides — green: did well · gold: worked, but cost something · dotted: where that cost surfaces
              </h3>
              <ul className="grid gap-2.5 text-sm leading-relaxed">
                {item.spans.map((s) => (
                  <li key={s.id} className="grid grid-cols-[2rem_1fr] gap-2">
                    <b className={cn('pt-0.5 text-xs font-extrabold', s.kind === 'admirable' ? 'text-emerald-800' : 'text-amber-800')}>{s.id}</b>
                    <span>
                      {s.note}
                      {s.cost_evidence && (
                        <span className="mt-0.5 block text-xs text-how-text-secondary">
                          <span className="font-bold text-amber-800">↳ cost: </span>
                          {s.cost_evidence}
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-8">
            <label htmlFor="review-note" className="mb-1 block text-[0.7rem] font-bold uppercase tracking-[0.13em] text-how-text-secondary">
              Your note on this one
            </label>
            <textarea
              id="review-note"
              value={notes[item.id] || ''}
              onChange={(e) => {
                const next = { ...notes, [item.id]: e.target.value };
                setNotes(next);
                saveNotes(next);
              }}
              placeholder="What read wrong, what read right, what you'd change."
              className="min-h-[6rem] w-full max-w-[64ch] rounded-xl border border-how-space-deep/15 bg-white p-3 text-sm leading-relaxed text-how-text-on-paper focus:outline-none focus:ring-2 focus:ring-how-accent-warm"
            />
          </div>

          <div className="mt-10 flex justify-between gap-4 border-t border-how-space-deep/10 pt-5">
            <button
              type="button"
              onClick={() => go(pos - 1)}
              disabled={pos === 0}
              className="inline-flex max-w-[48%] items-center gap-2 truncate rounded-full border border-how-space-deep/15 bg-white px-4 py-2.5 text-sm font-semibold text-how-space-deep disabled:opacity-40"
            >
              <ArrowLeft className="h-4 w-4 flex-none" />
              {pos > 0 ? `${items[pos - 1].level} · ${items[pos - 1].leader}` : 'Start'}
            </button>
            <button
              type="button"
              onClick={markReadAndNext}
              className="inline-flex max-w-[52%] items-center gap-2 truncate rounded-full bg-how-space-deep px-4 py-2.5 text-sm font-semibold text-white"
            >
              {pos < items.length - 1 ? `Mark read · ${items[pos + 1].level} · ${items[pos + 1].leader}` : 'Mark read · finish'}
              <ArrowRight className="h-4 w-4 flex-none" />
            </button>
          </div>
        </main>
      </div>
    </div>
  );
}

function Beat({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-2 mt-8 flex items-center gap-3 text-[0.68rem] font-bold uppercase tracking-[0.14em] text-how-accent-warm">
      {children}
      <span className="h-px flex-1 bg-how-space-deep/10" />
    </div>
  );
}

function Quote({ children }: { children: React.ReactNode }) {
  return <p className="my-1.5 border-l-2 border-how-accent-warm/30 pl-4 font-serif text-[1.1rem] leading-snug text-how-space-deep">{children}</p>;
}
